import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';

export type MotivoAvisoId =
  | 'COMPORTAMENTO_SALA'
  | 'DESRESPEITO_PROFESSOR'
  | 'CONFLITO_ALUNOS'
  | 'ELOGIO_DESTAQUE'
  | 'SOLICITAR_CONVERSA';

export interface MotivoAvisoCatalogo {
  id: MotivoAvisoId;
  label: string;
  categoria: string;
  nivelAtencao: string;
}

export interface ResponsavelAviso {
  id: string;
  nome: string;
  email?: string | null;
  telefone?: string | null;
  principal: boolean;
  recebeNotificacao: boolean;
  grauParentesco?: string | null;
}

export function useMotivosAviso() {
  const escolaId = useAuthStore((s) => s.escolaId);
  return useQuery({
    queryKey: ['avisos-pedagogicos', 'catalogo', escolaId],
    queryFn: async () => {
      const { data } = await api.get<MotivoAvisoCatalogo[]>(
        `/escolas/${escolaId}/avisos-pedagogicos/catalogo`,
      );
      return data;
    },
    enabled: !!escolaId,
    staleTime: 60_000 * 60,
  });
}

export function useResponsaveisAviso(alunoId: string | null, enabled = true) {
  const escolaId = useAuthStore((s) => s.escolaId);
  return useQuery({
    queryKey: ['avisos-pedagogicos', 'responsaveis', escolaId, alunoId],
    queryFn: async () => {
      const { data } = await api.get<ResponsavelAviso[]>(
        `/escolas/${escolaId}/avisos-pedagogicos/alunos/${alunoId}/responsaveis`,
      );
      return data;
    },
    enabled: !!escolaId && !!alunoId && enabled,
  });
}

export function useAvisarResponsavel() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      alunoId: string;
      responsavelId: string;
      motivo: MotivoAvisoId;
      complemento?: string;
    }) => {
      const { data } = await api.post(
        `/escolas/${escolaId}/avisos-pedagogicos`,
        payload,
      );
      return data as {
        observacaoId: string;
        preview: { titulo: string; mensagem: string };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avisos-pedagogicos'] });
      queryClient.invalidateQueries({ queryKey: ['observacoes'] });
    },
  });
}
